/**
 * @fileoverview Collect the name and avatar that complete a pending user's onboarding.
 * `/api/onboarding/complete` (`kc-internal-backend/functions/auth.py`) links the login
 * to the unique employee with the same email, or creates an active non-admin employee
 * when none exists; duplicate employee emails block completion. The response refreshes
 * `kc-internal-frontend/src/AuthContext.js`, then `kc-internal-frontend/src/authRedirect.js`
 * restores the saved destination. Completing onboarding grants no workspace sections;
 * the allowlist in `AppAccessContext.js` still applies.
 */

import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AppButton, AppTextInput } from './shared'
import { AuthContext } from '../AuthContext'
import ToastContext from '../ToastContext'
import api from '../api'
import ProfileAvatarUploader from './shared/ProfileAvatarUploader'
import {
    clearStoredPostLoginRedirectPath,
    getStoredPostLoginRedirectPath,
} from '../authRedirect'
import kcLogo from '../assets/img/Logo_1749325971610.png'
import './Onboarding.css'



function Onboarding() {
    const { applyAuthState, clearAuthState } = useContext(AuthContext)
    const showToast = useContext(ToastContext)
    const navigate = useNavigate()

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [user, setUser] = useState(null)
    const [name, setName] = useState('')
    const [error, setError] = useState('')

    const fetchUser = useCallback(

    async () => {
        setLoading(true)
        try {
            const response = await api.get('/user')
            const payload = response.data || {}
            setUser(payload)
            setName(payload.EmployeeName || payload.Name || '')
        } catch (requestError) {
            console.error(requestError)
            setError('Your onboarding details could not be loaded.')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        document.title = 'Complete Your Profile'
        fetchUser()
    }, [fetchUser])

    const handleSubmit = useCallback(

    async (event) => {
        event.preventDefault()

        const trimmedName = name.trim()
        if (!trimmedName) {
            setError('Name is required')
            return
        }

        setSaving(true)
        setError('')

        try {
            const response = await api.post('/onboarding/complete', {
                Name: trimmedName,
            })

            applyAuthState(response.data)
            showToast({ severity: 'success', summary: 'Successful', detail: 'Profile setup complete' })

            const redirectPath = getStoredPostLoginRedirectPath()
            clearStoredPostLoginRedirectPath()
            navigate(redirectPath, { replace: true })
        } catch (requestError) {
            console.error(requestError)
            const detail = requestError?.response?.data?.error || 'Your profile could not be completed.'
            setError(detail)
            showToast({ severity: 'error', summary: 'Error', detail })
        } finally {
            setSaving(false)
        }
    }, [applyAuthState, name, navigate, showToast])

    const handleLogout = useCallback(

    async () => {
        try {
            await api.post('/logout')
        } catch (requestError) {
            console.error(requestError)
        } finally {
            clearStoredPostLoginRedirectPath()
            clearAuthState()
            navigate('/login', { replace: true })
        }
    }, [clearAuthState, navigate])

    const onboardingBlockedReason = user?.onboardingBlockedReason || null
    const isDuplicateEmailBlocked = onboardingBlockedReason === 'duplicate_employee_email'

    return (
        <div className="onboarding-page">
            <div className="onboarding-shell">
                <div className="onboarding-brand">
                    <img src={kcLogo} alt="Kneupper & Covey" className="onboarding-logo" />
                    <div>
                        <div className="onboarding-kicker">First Sign-In</div>
                        <h1 className="onboarding-title">Set up your internal profile</h1>
                    </div>
                </div>

                <div className="onboarding-card">
                    {loading ? (
                        <div className="onboarding-loading">Loading your profile…</div>
                    ) : (
                        <>
                            <div className="onboarding-summary">
                                <div>
                                    <div className="onboarding-section-title">Account</div>
                                    <div className="onboarding-email">{user?.Email}</div>
                                </div>
                                <AppButton variant="secondary" icon="pi pi-sign-out" label="Sign Out" onClick={handleLogout} />
                            </div>

                            {isDuplicateEmailBlocked ? (
                                <div className="onboarding-alert" role="alert">
                                    <div className="onboarding-section-title">Admin action required</div>
                                    <p>
                                        Your Google Workspace email matches multiple people records. An admin needs to resolve the duplicate employee emails in People before you can finish onboarding.
                                    </p>
                                </div>
                            ) : null}

                            <form className="onboarding-form" onSubmit={handleSubmit}>
                                <div className="onboarding-field">
                                    <label htmlFor="onboarding-name" className="onboarding-label">Name</label>
                                    <AppTextInput
                                        id="onboarding-name"
                                        value={name}
                                        onChange={(event) => setName(event.target.value)}
                                        placeholder="Your display name"
                                        disabled={saving || isDuplicateEmailBlocked}
                                        data-lpignore="true"
                                        autoComplete="off"
                                    />
                                </div>

                                <div className="onboarding-field">
                                    <div className="onboarding-label">Profile Photo</div>
                                    <ProfileAvatarUploader
                                        avatarUrl={user?.ProfilePic}
                                        name={name || user?.Name}
                                        onAvatarChange={(url) => setUser((previous) => ({ ...previous, ProfilePic: url }))}
                                        buttonLabel={user?.ProfilePic ? 'Change Photo' : 'Add Photo'}
                                        size={88}
                                        helperText="Optional for now. You can always update it later in Settings."
                                        disabled={isDuplicateEmailBlocked}
                                    />
                                </div>

                                {error ? (
                                    <div className="onboarding-error" role="alert">{error}</div>
                                ) : null}

                                <div className="onboarding-actions">
                                    <AppButton
                                        type="submit"
                                        icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                                        label={saving ? 'Saving…' : 'Complete Setup'}
                                        disabled={saving || isDuplicateEmailBlocked}
                                    />
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

export default Onboarding
